import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Divider, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  formatMinor, marketplaceCategoryLabel, ROYALTY_RATE_PCT,
  type Artwork, type Designer, type DesignerEarning,
} from '@eprintsmart/shared';
import {
  getDesigner, moderateArtwork, watchArtworkByDesigner, watchEarningsByDesigner,
} from '../services/marketplace';
import { inviteArtist } from '../services/notifications';

/**
 * One artist, everything about them.
 *
 * Shows work in EVERY state, not just what is live. The reason to open an
 * artist is usually that something needs deciding — a suspended piece, a
 * missing PAN, work that has sold nothing — and a page that shows only the
 * published grid is the same page as their public one, which you could just
 * have visited.
 */

const STATUS_COLOUR = {
  PUBLISHED: 'success',
  SUSPENDED: 'warning',
  REMOVED: 'error',
} as const;

export function ArtistDetail() {
  const { id = '' } = useParams();
  const [designer, setDesigner] = useState<Designer | null>(null);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [earnings, setEarnings] = useState<DesignerEarning[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => { void getDesigner(id).then(setDesigner); }, [id]);
  useEffect(() => (id ? watchArtworkByDesigner(id, setArtworks) : undefined), [id]);
  useEffect(() => (id ? watchEarningsByDesigner(id, setEarnings) : undefined), [id]);

  const act = async (artworkId: string, action: 'RESTORE' | 'REMOVE') => {
    setBusy(true);
    setError(null);
    try {
      await moderateArtwork({ artworkId, action });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!designer) return;
    setBusy(true);
    setError(null);
    try {
      await inviteArtist({ email: designer.email });
      setNotice(`Invite resent to ${designer.email}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend that invite.');
    } finally {
      setBusy(false);
    }
  };

  if (!designer) {
    return <Typography variant="body2" color="text.secondary">Loading…</Typography>;
  }

  // Voided earnings are excluded from the total but still listed below, because
  // an artist who saw a sale appear should be able to see why it went away.
  const live = earnings.filter((e) => !(e as DesignerEarning & { voidedAt?: number }).voidedAt);
  const totalMinor = live.reduce((sum, e) => sum + e.royaltyMinor, 0);
  const unsettled = live.filter((e) => !e.settlementId);
  const unsettledMinor = unsettled.reduce((sum, e) => sum + e.royaltyMinor, 0);

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <Box>
        <Button component={RouterLink} to="/artists" startIcon={<ArrowBackIcon />} size="small">
          All artists
        </Button>
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <Typography variant="h5">{designer.displayName}</Typography>
          <Chip size="small" label={designer.status.toLowerCase()}
            color={designer.status === 'ACTIVE' ? 'success' : 'default'} />
          {designer.featured && <Chip size="small" label="featured" color="primary" variant="outlined" />}
          {/* The PAN decides the TDS rate — 10% with, 20% without. A missing one
              costs the artist money, so it is flagged wherever they appear. */}
          {!designer.pan && <Chip size="small" label="no PAN — TDS 20%" color="warning" />}
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {designer.email} · <a href={`/artist/${designer.handle}`} target="_blank" rel="noreferrer">
            /artist/{designer.handle}
          </a>
        </Typography>
        {designer.about && (
          <Typography variant="body2" mt={1} sx={{ maxWidth: '44rem' }}>{designer.about}</Typography>
        )}
        <Stack direction="row" spacing={1} mt={2}>
          <Button size="small" variant="outlined" disabled={busy} onClick={() => void resend()}>
            Resend invite
          </Button>
        </Stack>
      </Box>

      <Divider />

      <Stack direction="row" spacing={4}>
        <Box>
          <Typography variant="h5">{formatMinor(totalMinor)}</Typography>
          <Typography variant="caption" color="text.secondary">
            earned ({ROYALTY_RATE_PCT}% royalty)
          </Typography>
        </Box>
        <Box>
          <Typography variant="h5">{formatMinor(unsettledMinor)}</Typography>
          <Typography variant="caption" color="text.secondary">
            owed across {unsettled.length} sale{unsettled.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h5">
            {artworks.filter((a) => a.status === 'PUBLISHED').length}
            <Typography component="span" variant="body2" color="text.secondary">
              {' '}/ {artworks.length}
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">live artwork</Typography>
        </Box>
      </Stack>

      <Box>
        <Typography variant="h6" gutterBottom>Artwork ({artworks.length})</Typography>
        <Paper variant="outlined">
          {artworks.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                Nothing uploaded yet. They sign in at the storefront and upload from the artist
                studio — nobody can upload on their behalf.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={72} />
                  <TableCell>Title</TableCell>
                  <TableCell>Sells on</TableCell>
                  <TableCell align="right">Sold</TableCell>
                  <TableCell align="right">Status</TableCell>
                  <TableCell align="right">Moderate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {artworks.map((art) => {
                  const sales = live.filter((e) => e.artworkId === art.id);
                  const src = art.previews?.thumb ?? art.previews?.md;
                  return (
                    <TableRow key={art.id} hover>
                      <TableCell>
                        {src ? (
                          <img src={src} alt={art.title} width={56} height={56}
                            style={{ objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            no preview
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{art.title}</Typography>
                        {art.tags.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {art.tags.slice(0, 4).join(' · ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {/* Empty means "everything", which is what every piece
                            uploaded before the studio had a picker says. Worth
                            distinguishing from a deliberate choice. */}
                        {art.categoryIds.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            everything (not specified)
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {art.categoryIds.map((c) => (
                              <Chip key={c} size="small" variant="outlined"
                                label={marketplaceCategoryLabel(c)} />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {sales.length > 0
                          ? `${sales.length} · ${formatMinor(sales.reduce((s, e) => s + e.royaltyMinor, 0))}`
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={art.status.toLowerCase()}
                          color={STATUS_COLOUR[art.status] ?? 'default'} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {art.status !== 'PUBLISHED' && (
                            <Button size="small" disabled={busy}
                              onClick={() => void act(art.id, 'RESTORE')}>Put back</Button>
                          )}
                          {art.status !== 'REMOVED' && (
                            <Button size="small" color="error" disabled={busy}
                              onClick={() => void act(art.id, 'REMOVE')}>Remove</Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom>Sales ({earnings.length})</Typography>
        <Paper variant="outlined">
          {earnings.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">Nothing sold yet.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Royalty</TableCell>
                  <TableCell align="right">Settled</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {earnings.map((e) => {
                  const voided = (e as DesignerEarning & { voidedAt?: number }).voidedAt;
                  return (
                    <TableRow key={e.id} sx={voided ? { opacity: 0.5 } : undefined}>
                      <TableCell>
                        <RouterLink to={`/orders/${e.orderId}`}>{e.orderNumber}</RouterLink>
                      </TableCell>
                      <TableCell>{e.productName}</TableCell>
                      <TableCell align="right">{e.qty}</TableCell>
                      <TableCell align="right">
                        {formatMinor(e.royaltyMinor)}
                        {voided && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            voided — refunded
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {e.settlementId
                          ? <Chip size="small" label="paid" color="success" variant="outlined" />
                          : <Typography variant="caption" color="text.secondary">owed</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>
    </Stack>
  );
}
